import { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AuthContext } from '../utils/AuthContext';
import API from '../utils/API';
import PageTitle from '../components/PageTitle';
import './subscription.css';

const FEATURE_BANNERS = {
  individual: {
    icon: 'bi-stars',
    headline: "You're one step away from full legislative access",
    body: "Get your personalized Dashboard with hearings and news highlights, search and track bills across Legislation, and explore detailed Bill Profiles — all in one place.",
  },
  workplace: {
    icon: 'bi-rocket-takeoff',
    headline: "Level up how your whole team follows legislation",
    body: "Workspace unlocks shared bill tracking, client management, and team admin tools — everything you need to keep your team coordinated and ahead of the curve.",
  },
};

const PLANS = [
  {
    key: 'individual',
    icon: 'bi-person-check',
    title: 'Individual',
    subtitle: 'Full access to legislative tools',
    monthly: { price: '$14.99', label: '/month' },
    annual:  { price: '$152.99', label: '/year', savings: 'Save 15%' },
    features: [
      'Dashboard — hearings, news & legislative counts',
      'Legislation — search, filter & track bills',
      'Bill Profiles — full bill details & analysis',
    ],
  },
  {
    key: 'workplace',
    icon: 'bi-people',
    title: 'Workspace',
    subtitle: 'Everything in Individual, plus team tools',
    monthly: { price: '$74.99', base: 74.99, label: '/month', perSeat: 14.99 },
    annual:  { price: '$764.99', base: 764.99, label: '/year', savings: 'Save 15%', perSeat: 152.99 },
    features: [
      'All Individual features',
      'Workspaces — shared bill tracking for your team',
      'Client management & bill assignments',
      'Team admin controls',
    ],
  },
];

export default function Subscription() {
  const { auth, individualSubscription, workplaceSubscriber } = useContext(AuthContext);
  const [searchParams] = useSearchParams();
  const [billing, setBilling] = useState('monthly');
  // Persisted so bouncing back from Lemon Squeezy's checkout (which has no way to revise
  // quantity in-place) doesn't reset the picker to 1 and force a re-selection.
  const [seats, setSeats] = useState(() => {
    const stored = parseInt(localStorage.getItem('workspace_seats'), 10);
    return Number.isInteger(stored) && stored >= 1 ? stored : 1;
  });
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(null); // null = loading

  const banner = FEATURE_BANNERS[searchParams.get('plan')];
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    API.getAppSettings()
      .then((res) => setSubscriptionsEnabled(res.data.subscriptions_enabled))
      .catch(() => setSubscriptionsEnabled(false));
  }, []);

  useEffect(() => {
    localStorage.setItem('workspace_seats', String(seats));
  }, [seats]);

  const handleSubscribe = async (planKey) => {
    if (!auth.user) return;
    setError(null);
    setLoadingPlan(planKey);
    try {
      const res = await API.createCheckout({
        plan_type: planKey,
        billing,
        seats: planKey === 'workplace' ? seats : undefined,
        redirect_url: `${window.location.origin}/dashboard`,
      });
      const url = res.data?.checkout_url;
      if (!url) throw new Error('No checkout URL returned');

      window.LemonSqueezy?.Url?.Close?.();

      if (window.LemonSqueezy?.Url?.Open) {
        window.LemonSqueezy.Url.Open(url);
      } else {
        window.location.href = url;
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoadingPlan(null);
    }
  };

  // "active" = the user is the subscriber (pays or was granted access directly)
  // Members of a subscribed workspace have ACCESS but can still subscribe themselves
  const isActive = (planKey) =>
    planKey === 'individual' ? individualSubscription : workplaceSubscriber;

  if (subscriptionsEnabled === null) return null;

  return (
    <div className="subscribe-page">
      <PageTitle title="Subscribe | 'elloGovna" />
      {banner && (
        <div className="subscribe-feature-banner">
          <i className={`bi ${banner.icon} subscribe-feature-banner-icon`} />
          <div>
            <p className="subscribe-feature-banner-headline">{banner.headline}</p>
            <p className="subscribe-feature-banner-body">{banner.body}</p>
          </div>
        </div>
      )}

      <div className="subscribe-header">
        <h1 className="subscribe-title"><i>'ello</i>Govna Plans</h1>
        {subscriptionsEnabled ? (
          <>
            <p className="subscribe-tagline">Choose the plan that fits your work</p>
            <div className="subscribe-trial-note">
              <i className="bi bi-gift-fill" /> All plans include a <strong>14-day free trial</strong> — full access, no charge until your trial ends.
            </div>
            <br/>
            <div className="billing-toggle">
              <button
                className={`billing-btn ${billing === 'monthly' ? 'active' : ''}`}
                onClick={() => setBilling('monthly')}
              >
                Monthly
              </button>
              <button
                className={`billing-btn ${billing === 'annual' ? 'active' : ''}`}
                onClick={() => setBilling('annual')}
              >
                Annual
              </button>
            </div>
          </>
        ) : (
          <p className="subscribe-tagline">
            Subscriptions are launching soon — access is currently by invitation only.
          </p>
        )}
      </div>

      {error && (
        <div className="subscribe-error">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      <div className="subscribe-cards">
        {PLANS.map((plan) => {
          const active = isActive(plan.key);
          const priceInfo = billing === 'annual' ? plan.annual : plan.monthly;
          const isWorkplace = plan.key === 'workplace';
          const displayPrice = isWorkplace
            ? `$${(priceInfo.base + (seats - 1) * priceInfo.perSeat).toFixed(2)}`
            : priceInfo.price;

          return (
            <div
              key={plan.key}
              className={`subscribe-card ${plan.key === 'workplace' ? 'subscribe-card--workplace' : ''} ${active ? 'subscribe-card--active' : ''}`}
            >
              {active && (
                <div className="subscribe-card-badge">Current Plan</div>
              )}

              <div className="subscribe-card-icon">
                <i className={`bi ${plan.icon}`} />
              </div>

              <h2 className="subscribe-card-title">{plan.title}</h2>
              <p className="subscribe-card-subtitle">{plan.subtitle}</p>

              {subscriptionsEnabled && (
                <>
                  <div className="subscribe-card-price">
                    <span className="price-amount">{displayPrice}</span>
                    <span className="price-period">{priceInfo.label}</span>
                  </div>
                  {billing === 'annual' && (
                    <div className="price-savings">{priceInfo.savings}</div>
                  )}
                </>
              )}

              {subscriptionsEnabled && isWorkplace && (
                <div className="subscribe-seat-picker">
                  <span className="seat-picker-label">Seats</span>
                  <div className="seat-stepper">
                    <button
                      type="button"
                      onClick={() => setSeats((s) => Math.max(1, s - 1))}
                      disabled={seats <= 1 || active}
                      aria-label="Remove a seat"
                    >
                      &minus;
                    </button>
                    <span className="seat-count">{seats}</span>
                    <button
                      type="button"
                      onClick={() => setSeats((s) => s + 1)}
                      disabled={active}
                      aria-label="Add a seat"
                    >
                      +
                    </button>
                  </div>
                  <p className="seat-picker-note">
                    {seats === 1 ? 'Just you to start' : `You + ${seats - 1} teammate${seats - 1 === 1 ? '' : 's'}`}
                    {' — '}${priceInfo.perSeat.toFixed(2)}{priceInfo.label} per additional seat
                  </p>
                </div>
              )}

              <ul className="subscribe-card-features">
                {plan.features.map((f) => (
                  <li key={f}>
                    <i className="bi bi-check2" />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                className={`subscribe-card-btn ${active ? 'subscribe-card-btn--current' : ''} ${!subscriptionsEnabled && !active ? 'subscribe-card-btn--soon' : ''}`}
                onClick={() => subscriptionsEnabled && handleSubscribe(plan.key)}
                disabled={active || loadingPlan === plan.key || !subscriptionsEnabled}
              >
                {loadingPlan === plan.key
                  ? 'Loading…'
                  : active
                  ? 'Active'
                  : subscriptionsEnabled
                  ? 'Start Free Trial'
                  : 'Coming Soon'}
              </button>
            </div>
          );
        })}
      </div>

      <p className="subscribe-footer">
        {subscriptionsEnabled
          ? 'Subscriptions are managed through Lemon Squeezy. You can cancel anytime from your billing portal.'
          : 'Reach out to info@ellogovna.com to request early access.'}
      </p>
    </div>
  );
}
